import React, { useMemo } from "react";
import {
  isEventExternalLink,
  isExternalEventbrite,
  isExternalLinkNoData,
  isExternalShowclix,
  isProductSpecificType,
} from "@markit/common.utils";
import { getEventState } from "../../../redux/slices/eventSlice";
import { useSelector } from "react-redux";
import { Event, EventType, OrganizerType } from "@markit/common.types";
import { Colors } from "../../../utils/colors";
import LabelButton from "../../Buttons/LabelButton";

type EventItemPreviewTypeProps = {
  eventItem: Event;
  altBackgroundColor?: string;
  altFontSize?: number;
};

// The text box that is displayed with the event item preview to denote hosting, co-hosting, or external link
const EventItemPreviewType = ({
  eventItem,
  altBackgroundColor,
  altFontSize,
}: EventItemPreviewTypeProps) => {
  const { tickets } = useSelector(getEventState).events;

  const userTicket = useMemo(
    () =>
      tickets.find((ticket) => {
        return ticket.eventId === eventItem.id;
      }),
    [eventItem.id, tickets]
  );

  const organizerType = useMemo(
    () =>
      userTicket && userTicket.role
        ? userTicket.role.type === OrganizerType.HOST
          ? "Hosting"
          : userTicket.role.type === OrganizerType.COHOST
            ? "Co-Hosting"
            : userTicket.role.type === OrganizerType.SCANNER
              ? "Scanning"
              : userTicket.role.type === OrganizerType.PERFORMER
                ? "Performing"
                : userTicket.role.type === OrganizerType.PROMOTER
                  ? "Promoting"
                  : userTicket.role.type ===
                      OrganizerType.ALTERNATEPAYMENTCOLLECTOR
                    ? "Payment Collector"
                    : "Organizing"
        : "Organizing",
    [userTicket]
  );

  const colorTypes = useMemo(() => {
    if (isExternalEventbrite(eventItem.eventType)) {
      return {
        backgroundColor: "rgba(250, 99, 69, 0.1)",
        borderColor: Colors.ORANGE_EB,
        color: Colors.ORANGE_EB,
      };
    } else if (isExternalShowclix(eventItem.eventType)) {
      return {
        backgroundColor: "rgba(0, 122, 255, 0.1)",
        borderColor: Colors.BLUE5,
        color: Colors.BLUE5,
      };
    } else {
      return {
        backgroundColor: Colors.WHITE1,
        borderColor: Colors.GRAY2,
        color: Colors.BLACK,
      };
    }
  }, [eventItem.eventType]);

  const getLabelText = useMemo(() => {
    if (isProductSpecificType(eventItem.eventType)) {
      return eventItem.eventType;
    } else if (eventItem.eventType === EventType.GENERIC_LINK) {
      return "Form";
    } else if (isExternalEventbrite(eventItem.eventType)) {
      return "Eventbrite";
    } else if (isExternalShowclix(eventItem.eventType)) {
      return "Showclix";
    } else if (isEventExternalLink(eventItem.eventType)) {
      return "Linked Event";
    }
    return organizerType;
  }, [eventItem.eventType, organizerType]);

  return !isExternalLinkNoData(eventItem.eventType) ? (
    <LabelButton
      color={colorTypes.color}
      backgroundColor={altBackgroundColor ?? colorTypes.backgroundColor}
      text={getLabelText}
      borderColor={colorTypes.borderColor}
      containerStyles={{
        fontSize: altFontSize ?? 12,
        cursor: "pointer",
        borderRadius: 8,
        paddingInline: 8,
        paddingBlock: 5,
        fontWeight: 400,
      }}
    />
  ) : null;
};

export default EventItemPreviewType;
